/*
  NDI Header JS
  --------------------------------------------------------------
  - Updates cart bubble count + total when Shopify's pub/sub
    emits a cart update.
  - Mobile utility bar collapse/expand.
  - Mobile hamburger toggles the menu drawer (uses Dawn's
    existing <menu-drawer> custom element when present).
*/

(function () {
  'use strict';

  // ── Cart bubble + total ─────────────────────────────────────
  function formatMoney(cents) {
    if (typeof Shopify !== 'undefined' && typeof Shopify.formatMoney === 'function') {
      return Shopify.formatMoney(cents);
    }
    return '$' + (cents / 100).toFixed(2);
  }

  function updateCartUI(cart) {
    if (!cart) return;
    var bubble = document.querySelector('.ndi-pill--cart .ndi-pill__bubble');
    var value = document.querySelector('.ndi-pill--cart .ndi-pill__value');
    if (bubble) {
      bubble.textContent = cart.item_count > 0 ? cart.item_count : '';
      bubble.setAttribute('data-count', cart.item_count);
    }
    if (value && cart.total_price != null) {
      value.textContent = formatMoney(cart.total_price);
    }
  }

  function fetchCart() {
    fetch(window.Shopify ? window.Shopify.routes.root + 'cart.js' : '/cart.js')
      .then(function (r) { return r.json(); })
      .then(updateCartUI)
      .catch(function () { /* silent — header just won't update */ });
  }

  // Native cart:updated event (dispatched by ndi-component-cart-drawer.js on every
  // add/change). Replaced Dawn's PUB_SUB_EVENTS.cartUpdate — no pubsub.js dependency.
  function bindCartEvents() {
    document.addEventListener('cart:updated', fetchCart);
  }

  // ── Utility bar collapse ────────────────────────────────────
  function bindUtility() {
    var toggle = document.querySelector('.ndi-utility__toggle');
    var utility = document.querySelector('.ndi-utility');
    if (!toggle || !utility) return;
    toggle.addEventListener('click', function () {
      // data-expanded on the container (a plain <div> can't take aria-expanded);
      // the toggle button keeps the real aria-expanded.
      var expanded = utility.getAttribute('data-expanded') === 'true';
      utility.setAttribute('data-expanded', expanded ? 'false' : 'true');
      toggle.setAttribute('aria-expanded', expanded ? 'false' : 'true');
    });
  }

  // ── Utility bar dropdowns ───────────────────────────────────
  function bindUtilityDropdowns() {
    var dropdowns = document.querySelectorAll('[data-utility-dropdown]');
    if (!dropdowns.length) return;

    dropdowns.forEach(function (details) {
      details.addEventListener('toggle', function () {
        if (!details.open) return;
        dropdowns.forEach(function (other) {
          if (other !== details) other.open = false;
        });
      });
    });

    // Hover-open for pointer devices (desktop). Touch/coarse pointers
    // keep native click behaviour. A short close delay bridges the gap
    // between the trigger and the panel so it doesn't snap shut.
    var hoverQuery = window.matchMedia('(hover: hover) and (pointer: fine)');
    dropdowns.forEach(function (details) {
      var closeTimer = null;
      details.addEventListener('mouseenter', function () {
        if (!hoverQuery.matches) return;
        if (closeTimer) { clearTimeout(closeTimer); closeTimer = null; }
        details.open = true;
      });
      details.addEventListener('mouseleave', function () {
        if (!hoverQuery.matches) return;
        closeTimer = setTimeout(function () { details.open = false; }, 150);
      });
    });

    document.addEventListener('click', function (event) {
      dropdowns.forEach(function (details) {
        if (details.open && !details.contains(event.target)) {
          details.open = false;
        }
      });
    });

    document.addEventListener('keydown', function (event) {
      if (event.key !== 'Escape') return;
      dropdowns.forEach(function (details) {
        details.open = false;
      });
    });
  }

  // ── Utility bar rotating messages (tablet/laptop only) ──────
  // Desktop (≥1440px) shows all messages inline; mobile hides the bar.
  // Between 768–1439px only one fits, so we cycle through them.
  function bindUtilityMessages() {
    var container = document.querySelector('[data-utility-messages]');
    if (!container) return;
    var messages = container.querySelectorAll('.ndi-utility__message');
    if (messages.length < 2) return;

    var tabletQuery = window.matchMedia('(min-width: 768px) and (max-width: 1439.98px)');
    var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    var timer = null;
    var index = 0;

    function show(i) {
      for (var n = 0; n < messages.length; n++) {
        messages[n].classList.toggle('is-active', n === i);
      }
    }

    function start() {
      index = 0;
      show(0);
      if (timer || reduceMotion.matches) return; // static first message if reduced motion
      timer = setInterval(function () {
        index = (index + 1) % messages.length;
        show(index);
      }, 4000);
    }

    function stop() {
      if (timer) { clearInterval(timer); timer = null; }
      // Drop active states so laptop (inline) / mobile (hidden) CSS governs.
      for (var n = 0; n < messages.length; n++) {
        messages[n].classList.remove('is-active');
      }
    }

    function sync() {
      if (tabletQuery.matches) start();
      else stop();
    }

    tabletQuery.addEventListener('change', sync);
    sync();
  }

  // ── Off-canvas drawer (tablet/mobile nav) ───────────────────
  // Opened by the main-bar hamburger ([data-ndi-menu-toggle]); slides in via the
  // .is-open class. `hidden` is the closed/inaccessible state — we drop it before
  // animating in, and restore it after the slide-out transition completes.
  function bindDrawer() {
    var drawer = document.querySelector('[data-ndi-drawer]');
    if (!drawer) return;
    var panel = drawer.querySelector('.ndi-drawer__panel');
    var toggles = document.querySelectorAll('[data-ndi-menu-toggle]');
    if (!panel || !toggles.length) return;

    var lastFocused = null;

    function isOpen() { return drawer.classList.contains('is-open'); }

    function focusables() {
      return panel.querySelectorAll(
        'a[href], button:not([disabled]), summary, [tabindex]:not([tabindex="-1"])'
      );
    }

    function open(trigger) {
      lastFocused = trigger || document.activeElement;
      drawer.hidden = false;
      requestAnimationFrame(function () { drawer.classList.add('is-open'); });
      document.documentElement.classList.add('ndi-scroll-lock');
      toggles.forEach(function (t) { t.setAttribute('aria-expanded', 'true'); });
      var first = panel.querySelector('.ndi-drawer__close') || panel;
      first.focus();
    }

    function close() {
      if (!isOpen()) return;
      drawer.classList.remove('is-open');
      document.documentElement.classList.remove('ndi-scroll-lock');
      toggles.forEach(function (t) { t.setAttribute('aria-expanded', 'false'); });

      var done = function () {
        if (!drawer.classList.contains('is-open')) drawer.hidden = true;
        panel.removeEventListener('transitionend', done);
      };
      panel.addEventListener('transitionend', done);
      // Fallback when there's no transition (e.g. reduced motion).
      setTimeout(done, 400);

      if (lastFocused && typeof lastFocused.focus === 'function') lastFocused.focus();
    }

    toggles.forEach(function (t) {
      t.addEventListener('click', function (event) {
        event.preventDefault();
        if (isOpen()) close(); else open(t);
      });
    });

    drawer.querySelectorAll('[data-ndi-drawer-close]').forEach(function (el) {
      el.addEventListener('click', close);
    });

    document.addEventListener('keydown', function (event) {
      if (!isOpen()) return;
      if (event.key === 'Escape') { close(); return; }
      if (event.key !== 'Tab') return;
      var items = focusables();
      if (!items.length) return;
      var firstEl = items[0];
      var lastEl = items[items.length - 1];
      if (event.shiftKey && document.activeElement === firstEl) {
        event.preventDefault();
        lastEl.focus();
      } else if (!event.shiftKey && document.activeElement === lastEl) {
        event.preventDefault();
        firstEl.focus();
      }
    });

    // If the viewport grows to desktop (inline nav returns), close the drawer.
    var desktopQuery = window.matchMedia('(min-width: 1280px)');
    desktopQuery.addEventListener('change', function () {
      if (desktopQuery.matches) close();
    });
  }

  // ── Init ────────────────────────────────────────────────────
  NDI.ready(function () {
    bindCartEvents();
    bindUtility();
    bindUtilityDropdowns();
    bindUtilityMessages();
    bindDrawer();
  });
})();
